import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { JsonEditor } from "@/common";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { GoalField } from "@/components/GoalField";
import { TriggerIntroduction } from "@/features/triggers/components/TriggerGuidance";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { FieldWatch, useFormContext } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import TextField from "@imminently/decisively-ui/TextField";
import { get } from "lodash";
import { useCallback, useEffect, useState } from "react";

export const AttributeContactForm = ({ scope }: { scope: string }) => {
  const form = useFormContext<any>();
  const setFieldValue = (name: string, value: any) =>
    form.setValue(name, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  const canEdit = useIsProjectTechnicalWriter();
  const graph = useParsedGraph();
  const release = useFullReleaseStrict();
  const templateEntities = (release?.relationships ?? [])
    .map((relationship: any) => relationship?.name ?? relationship?.id)
    .filter((entity: unknown): entity is string => typeof entity === "string" && Boolean(entity.trim()));
  const [isMonacoReady, setIsMonacoReady] = useState(false);
  const [monacoError, setMonacoError] = useState<string | null>(null);

  useEffect(() => {
    // Set a timeout to ensure Monaco Editor has time to load
    const timer = setTimeout(() => {
      setIsMonacoReady(true);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const scopedValue = (name: string) => `${scope}.${name}`;

  const handleJsonEditorError = useCallback((error: Error) => {
    setMonacoError(error.message);
  }, []);

  const handleJsonChange = useCallback(
    (value: string) => {
      try {
        // Try to parse the JSON string into an object
        const parsedValue = JSON.parse(value);
        setFieldValue(`${scope}.template`, parsedValue);
      } catch (error) {
        // If parsing fails, store as string (for invalid JSON during editing)
        setFieldValue(`${scope}.template`, value);
      }
    },
    [setFieldValue, scope],
  );

  return (
    <FieldWatch names={[scopedValue("template")]}>
      {(values) => {
        const templateValue = get(values, scopedValue("template"), {});
        const templateString =
          typeof templateValue === "string" ? templateValue : JSON.stringify(templateValue, null, 2);
        return (
          <FormPanel>
            <TriggerIntroduction type="contact" />
            <FormPanel>
              <TextField
                label="Path"
                required
                name={scopedValue("path")}
                disabled={!canEdit}
                fullWidth
              />
              <GoalField
                label="Contact"
                name={scopedValue("contact")}
                disabled={!canEdit}
              />

              {monacoError ? (
                <div
                  style={{
                    height: "200px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #ff6b6b",
                    borderRadius: "4px",
                    backgroundColor: "#ffe0e0",
                    padding: "1rem",
                  }}
                >
                  <div style={{ color: "#d63031", marginBottom: "0.5rem" }}>JSON Editor Error: {monacoError}</div>
                  <textarea
                    value={templateString}
                    onChange={(e) => handleJsonChange(e.target.value)}
                    disabled={!canEdit}
                    style={{
                      width: "100%",
                      height: "150px",
                      fontFamily: "monospace",
                      fontSize: "12px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  />
                </div>
              ) : isMonacoReady ? (
                <div style={{ height: "300px", border: "1px solid #ccc", borderRadius: "4px" }}>
                  <JsonEditor
                    value={templateString}
                    onChange={handleJsonChange}
                    disabled={!canEdit}
                    graph={graph}
                    useDescription
                    templateAware
                    templateEntities={templateEntities}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                >
                  Loading JSON Editor...
                </div>
              )}
            </FormPanel>
          </FormPanel>
        );
      }}
    </FieldWatch>
  );
};
